/**
 * BriefingPage – Wochenübersicht vom KI-Assistenten Zeus
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Calendar, RefreshCw, AlertTriangle, CheckCircle, Clock,
  Mail, MessageCircle, FileText, ChevronDown, ChevronUp,
  Play, Brain, ListTodo, CalendarDays
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function BriefingPage() {
  const [briefing, setBriefing] = useState(null);
  const [allBriefings, setAllBriefings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const headers = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [latestRes, listRes] = await Promise.all([
        fetch(`${API}/api/briefing/latest`, { headers: headers() }),
        fetch(`${API}/api/briefing/list`, { headers: headers() }),
      ]);
      if (latestRes.ok) {
        const data = await latestRes.json();
        setBriefing(data.exists ? data : null);
      }
      if (listRes.ok) setAllBriefings(await listRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/api/briefing/generate`, { method: 'POST', headers: headers() });
      if (r.ok) {
        toast.success('Wochenübersicht erstellt');
        fetchData();
      }
    } catch (e) { toast.error('Fehler'); }
    setGenerating(false);
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Lädt...</div>;

  const d = briefing?.daten || {};

  return (
    <div className="space-y-6" data-testid="briefing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="w-7 h-7 text-amber-600" />
            Wochenübersicht
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            KI-generiertes Briefing &middot; Jeden Montag um 7:30 Uhr
          </p>
        </div>
        <Button onClick={handleGenerate} disabled={generating} className="bg-amber-600 hover:bg-amber-700" data-testid="briefing-generate-btn">
          {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          Jetzt generieren
        </Button>
      </div>

      {!briefing ? (
        <Card>
          <CardContent className="p-12 text-center text-slate-400">
            <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Noch kein Briefing vorhanden</p>
            <p className="text-sm mt-1">Klicken Sie "Jetzt generieren" oder warten Sie auf den nächsten Montag</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Status-Karten */}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-7">
            {[
              { label: 'Fristen', value: d.fristen_diese_woche?.length || 0, icon: AlertTriangle, bg: 'bg-red-50 text-red-700 border-red-200' },
              { label: 'Überfällig', value: d.aufgaben_ueberfaellig?.length || 0, icon: AlertTriangle, bg: 'bg-orange-50 text-orange-700 border-orange-200' },
              { label: 'Offen', value: d.aufgaben_offen?.length || 0, icon: ListTodo, bg: 'bg-blue-50 text-blue-700 border-blue-200' },
              { label: 'Termine', value: d.termine_diese_woche?.length || 0, icon: CalendarDays, bg: 'bg-violet-50 text-violet-700 border-violet-200' },
              { label: 'E-Mails', value: d.neue_emails || 0, icon: Mail, bg: 'bg-slate-50 text-slate-700 border-slate-200' },
              { label: 'WhatsApp', value: d.whatsapp_ungelesen || 0, icon: MessageCircle, bg: 'bg-green-50 text-green-700 border-green-200' },
              { label: 'Entwürfe', value: d.ki_entwuerfe_offen || 0, icon: FileText, bg: 'bg-amber-50 text-amber-700 border-amber-200' },
            ].map(s => (
              <div key={s.label} className={`rounded-lg border p-3 text-center ${s.bg}`}>
                <s.icon className="w-4 h-4 mx-auto mb-1" />
                <p className="text-xl font-bold">{s.value}</p>
                <p className="text-[10px]">{s.label}</p>
              </div>
            ))}
          </div>

          {/* KI-Zusammenfassung */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-amber-600" />
                Zeus' Wochenbriefing – KW {briefing.kalenderwoche}
                <span className="text-xs text-slate-400 font-normal ml-2">
                  {briefing.datum && new Date(briefing.datum).toLocaleString('de-DE')}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap" data-testid="briefing-ki-text">
                {briefing.ki_zusammenfassung}
              </div>
            </CardContent>
          </Card>

          {/* Detail-Karten */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Fristen */}
            {d.fristen_diese_woche?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Fristen diese Woche ({d.fristen_diese_woche.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {d.fristen_diese_woche.sort((a, b) => a.tage_bis - b.tage_bis).map((f, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded bg-red-50 text-sm">
                      <div>
                        <p className="font-medium">{f.klient}</p>
                        <p className="text-xs text-slate-500">{f.typ}</p>
                      </div>
                      <Badge variant={f.tage_bis <= 2 ? 'destructive' : 'secondary'} className="text-xs">
                        {f.tage_bis === 0 ? 'Heute!' : `in ${f.tage_bis}T`}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Überfällige Aufgaben */}
            {d.aufgaben_ueberfaellig?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    Überfällige Aufgaben ({d.aufgaben_ueberfaellig.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {d.aufgaben_ueberfaellig.slice(0, 10).map((a, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded bg-orange-50 text-sm">
                      <p className="font-medium truncate flex-1">{a.titel}</p>
                      <span className="text-xs text-orange-600 ml-2">Fällig: {a.faellig}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Termine */}
            {d.termine_diese_woche?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-violet-500" />
                    Termine ({d.termine_diese_woche.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {d.termine_diese_woche.map((t, i) => (
                    <div key={i} className="p-2 rounded bg-violet-50 text-sm">
                      <p className="font-medium">{t.titel}</p>
                      <p className="text-xs text-slate-500">{t.datum} {t.uhrzeit} {t.ort && `· ${t.ort}`}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Historie */}
          <div>
            <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              Frühere Briefings ({allBriefings.length})
            </Button>
            {showHistory && allBriefings.length > 1 && (
              <div className="mt-2 space-y-2">
                {allBriefings.slice(1).map(b => (
                  <Card key={b.id} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setBriefing(b)}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">KW {b.kalenderwoche} / {b.jahr}</p>
                        <p className="text-xs text-slate-400">{new Date(b.datum).toLocaleDateString('de-DE')}</p>
                      </div>
                      <div className="flex gap-2 text-xs text-slate-500">
                        <span>{b.fristen_count} Fristen</span>
                        <span>{b.aufgaben_offen_count} Aufgaben</span>
                        <span>{b.termine_count} Termine</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
